import React, { useState } from "react";
import {
  Alert,
  Box,
  Typography,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import AppButton from "../buttons/AppButton";
import AppCircularProgress from "../common/AppCircularProgress";
import ConfirmDialog from "../common/ConfirmDialog";
import AddSupplierContactForm from "../../forms/AddSupplierContactForm";

import { useAppDispatch } from "../../redux/store";
import { removeSupplierContact } from "../../redux/actions/supplierActions";
import { SupplierResponseDto } from "../../shared/dtos/supplier/SupplierResponseDto";

const ViewSupplierModal: React.FC<{
  open: boolean;
  onClose: () => void;
  supplier: SupplierResponseDto | null;
  loading: boolean;
  error: string | null;
}> = ({ open, onClose, supplier, loading, error }) => {
  const dispatch = useAppDispatch();
  const [showAddContact, setShowAddContact] = useState<boolean>(false);
  const [deleteContactId, setDeleteContactId] = useState<string | null>(null);

  const renderContent = (): React.ReactNode => {
    if (loading) return <AppCircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!supplier) return null;

    return (
      <Box>
        <Grid container spacing={2}>
          <DetailItem label="Company" value={supplier.name} />
          <DetailItem label="Code" value={supplier.supplierCode} />
          <DetailItem label="Tax PIN" value={supplier.taxId} />
          <DetailItem label="Email" value={supplier.email} />
          <DetailItem label="Phone" value={supplier.primaryPhone} />
          <DetailItem label="Website" value={supplier.website || "N/A"} />
        </Grid>

        <Divider sx={{ my: 2 }}>
          <Typography variant="overline">Address</Typography>
        </Divider>
        <Typography variant="body2">
          {supplier.address?.streetLine1},{" "}
          {supplier.address?.streetLine2 && `${supplier.address.streetLine2}, `}
          {supplier.address?.city}, {supplier.address?.country}
        </Typography>

        <Divider sx={{ my: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              gap: 2,
            }}
          >
            <Typography variant="overline">Contact Persons</Typography>
            <AppButton
              variant="text"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={() => setShowAddContact(!showAddContact)}
            >
              {showAddContact ? "Cancel" : "Add Contact"}
            </AppButton>
          </Box>
        </Divider>

        {showAddContact && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <AddSupplierContactForm
              supplierId={supplier.id}
              loading={loading}
              onSuccess={() => setShowAddContact(false)}
            />
          </Box>
        )}

        <List dense>
          {supplier.contacts?.length > 0 ? (
            supplier.contacts.map((c) => (
              <ListItem
                key={c.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    color="error"
                    onClick={() => setDeleteContactId(c.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={`${c.firstName} ${c.lastName} - ${c.jobTitle || "Staff"}`}
                  secondary={`${c.phone} | ${c.email || "No email"}`}
                />
              </ListItem>
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">
              No contacts registered.
            </Typography>
          )}
        </List>

        {/* Delete Contact Confirmation */}
        {deleteContactId && (
          <ConfirmDialog
            open={!!deleteContactId}
            onClose={() => setDeleteContactId(null)}
            onConfirm={() => {
              dispatch(removeSupplierContact(supplier.id, deleteContactId));
              setDeleteContactId(null);
            }}
            title="Remove Contact"
            description="Are you sure you want to remove this contact person?"
          />
        )}
      </Box>
    );
  };

  return (
    <AppModal open={open} onClose={onClose} title="Supplier Profile" width="md">
      {renderContent()}
    </AppModal>
  );
};

export default ViewSupplierModal;
